import React, { type ReactNode } from 'react';

import ProductItemCard from '@components/shared/items/ProductItemCard';
import { InputNumber, Row, Col, Form, type FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';

interface FormListField {
  key: number | string;
  name: number;
}

interface InventoryType {
  id: string;
  name?: string;
  code?: string;
  [key: string]: unknown;
}

interface InventoryAttributeDefinition {
  id?: string;
  name: string;
  label?: string;
  data_type?: string;
  [key: string]: unknown;
}

interface InboundItemsFormProps {
  fields: FormListField[];
  add: (defaultValue?: Record<string, unknown>, insertIndex?: number) => void;
  remove: (index: number | number[]) => void;
  form: FormInstance;
  productService: unknown;
  inventoryTypes?: InventoryType[];
  allInventoryTypes?: InventoryType[];
  inventoryAttributeDefinitions?: InventoryAttributeDefinition[];
  role?: 'admin' | 'operator';
}

const InboundItemsForm: React.FC<InboundItemsFormProps> = ({
  fields,
  add,
  remove,
  form,
  productService,
  inventoryTypes = [],
  allInventoryTypes = [],
  inventoryAttributeDefinitions = [],
  role = 'admin'
}) => {
  const { t } = useTranslation();

  // ProductItemCard 是 .tsx，但其 `attributeDefinitions` 的 `data_type` 為 literal union，
  // 與 service schema 的 `string` 不相容；沿用 OrderItemsForm 的 loose cast 暫橋接，
  // 後續修 ProductItemCard 的 Props interface 時一併清掉（見 OrderItemsForm 同款註解）。
  const ProductItemCardLoose = ProductItemCard as unknown as React.ComponentType<any>;

  // allInventoryTypes includes merchant-specific types (including disabled ones, for edit fallback display)
  const effectiveInventoryTypes = allInventoryTypes.length > 0 ? allInventoryTypes : inventoryTypes;

  const renderExtraFields = (
    name: number,
    restField: Record<string, unknown>,
  ): ReactNode => (
    <Row gutter={8} style={{ marginTop: 8 }}>
      <Col span={6}>
        <Form.Item
          {...restField}
          name={[name, 'scheduled_quantity']}
          label={t('shared.inbounds.form.scheduled_quantity')}
          rules={[{ required: true, message: t('common.validation.required') }]}
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      </Col>
    </Row>
  );

  return (
    <div>
      {fields && fields.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <span style={{ color: '#666' }}>
            {t('shared.inbounds.form.total_items', { count: fields.length })}
          </span>
        </div>
      )}

      {fields.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#999',
          border: '1px dashed #d9d9d9',
          borderRadius: '4px'
        }}>
          {t('shared.inbounds.form.no_items')}
        </div>
      ) : (
        fields.map(({ key, name, ...restField }) => (
          <ProductItemCardLoose
            key={key}
            restField={restField}
            name={name}
            form={form}
            onRemove={remove}
            searchProductsService={productService}
            inventoryTypes={effectiveInventoryTypes}
            attributeDefinitions={inventoryAttributeDefinitions}
            renderExtraFields={renderExtraFields}
            hideQuantity={true}
            listName="inbound_items_attributes"
            quantityFieldName="scheduled_quantity"
            role={role}
            inventoryAttributesOptional
          />
        ))
      )}
    </div>
  );
};

export default InboundItemsForm;
